/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  Modal,
  Box,
  Link,
  CircularProgress
} from "@mui/material";
import { addAttendanceActivityMark, addRemoteAttendance, getLastAttendance, getLastAttendanceActivity } from "../../../api/s10OrganizationApi";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import MySnackbar from "../../../components/MySnackbar";

interface Attendance {
  EmployeeId: number;
  FullName: string;
  DocumentNumber: string;
  Date: string;
  Time: string;
  MarkType: string;
  HasEntranceOnThisDay: boolean;
}

interface Activity {
  Id: number;
  EmployeeId: number;
  DateTimeActivity: string;
  DailyActivities: string;
}

interface ApiResponse<T> {
  Type: number;
  Message: string;
  Value: T | null;
  Validates: any;
}

export interface Location {
  latitude: number;
  longitude: number;
  accuracy?: number;
}

const AttendancePage: React.FC = () => {
  const { companyselected, connectionId } = useSelector((state: RootState) => state.auth);

  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [lastAttendance, setLastAttendance] = useState<Attendance | null>(null);
  const [dailyActivity, setDailyActivity] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");
  const [employeeId, setEmployeeId] = useState<number | null | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  const [location, setLocation] = useState<Location | null>(null);
  const [locationError, setLocationError] = useState<string>("");
  const [isLocationLoading, setIsLocationLoading] = useState<boolean>(true);
  const [mapModalOpen, setMapModalOpen] = useState<boolean>(false);
  const [shouldRetry, setShouldRetry] = useState(false);
  
  const IS_LOCATION_REQUIRED = true;

  const DEFAULT_ERROR_MESSAGE = "No estás autorizado para realizar marcaciones remotas.";

  const requestLocationPermission = async (): Promise<boolean> => {
    if (!navigator.geolocation) {
      setLocationError("Tu navegador no soporta geolocalización, por favor intenta con otro navegador.");
      return false;
    }

    return new Promise((resolve) => {
      navigator.permissions?.query({ name: "geolocation" })
        .then((permissionStatus) => {
          if (permissionStatus.state === "granted") {
            resolve(true);
          } else if (permissionStatus.state === "prompt") {
            resolve(true);
          } else {
            setLocationError("Permiso de ubicación denegado. Por favor, habilita la ubicación en tu navegador.");
            resolve(false);
          }
        })
        .catch(() => {
          resolve(true);
        });
    });
  };

  const getCurrentLocation = () => {
    setIsLocationLoading(true);
    setLocationError('');
    setLocation(null);
    setShouldRetry(false);
  
    const options = {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 0 
    };
  
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation: Location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        setLocation(newLocation);
        setIsLocationLoading(false);
      },
      (error) => {
        setIsLocationLoading(false);
        let errorMessage = '';
        let allowRetry = true;
  
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Permiso de ubicación denegado. ';
            errorMessage += 'Por favor, habilita la ubicación en la configuración de tu navegador o dispositivo y haz clic en "Reintentar".';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'La información de ubicación no está disponible. Reintentar podría ayudar.';
            break;
          case error.TIMEOUT:
            errorMessage = `La solicitud de ubicación tardó demasiado (${options.timeout/1000} segundos). `;
            errorMessage += 'Asegúrate de tener una buena señal (GPS/Wi-Fi) y haz clic en "Reintentar".';
            break;
          default:
            errorMessage = 'Ocurrió un error desconocido al obtener la ubicación.';
            break;
        }
        setLocationError(errorMessage);
        setShouldRetry(allowRetry);
      },
      options
    );
  };

  useEffect(() => {
    const initLocation = async () => {
      if (IS_LOCATION_REQUIRED) {
        const hasPermission = await requestLocationPermission();
        if (hasPermission) {
          getCurrentLocation();
        } else {
          setIsLocationLoading(false);
        }
      } else {
        setIsLocationLoading(false);
      }
    };

    initLocation();
  }, []);

  const areButtonsDisabled = () => {
    if (loading || !employeeId) return true;
    if (IS_LOCATION_REQUIRED && (!location || locationError)) return true;
    return false;
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchLastAttendance = async () => {
    const response: { data: ApiResponse<Attendance> } = await getLastAttendance();
    console.log(response);
    if (response.data.Type === 0 && response.data.Value) {
      setLastAttendance(response.data.Value);
      setEmployeeId(response.data.Value.EmployeeId);
    } else {
      setErrorMsg(response.data.Message || DEFAULT_ERROR_MESSAGE);
      setEmployeeId(null);
    }
  };

  const fetchDailyActivity = async () => {
    const response: { data: ApiResponse<Activity> } = await getLastAttendanceActivity();
    if (response.data.Type === 0 && response.data.Value) {
      setDailyActivity(response.data.Value.DailyActivities || "");
    } else {
      setErrorMsg(response.data.Message || "Error obteniendo actividades diarias.");
    }
  };

  const handleActivitySubmit = async () => {
    if (!employeeId) {
      setErrorMsg(DEFAULT_ERROR_MESSAGE);
      return;
    }
    if (!dailyActivity) {
      setErrorMsg("El campo de actividades diarias debe ser completado.");
      return;
    }
    const response = await addAttendanceActivityMark(employeeId, dailyActivity);
    if (response.data.Type === 0) {
      setDailyActivity("");
      setSuccessMsg("Actividad registrada con éxito.");
    } else {
      setErrorMsg(response.data.Message || "Ocurrió un error registrando la actividad.");
    }
  };

  const handleAttendanceMark = async (markType: 0 | 1) => {
    if (!employeeId) {
      setErrorMsg(DEFAULT_ERROR_MESSAGE);
      return;
    }

    if (IS_LOCATION_REQUIRED) {
      if (!location || locationError) {
        setErrorMsg("No se puede realizar la marcación sin ubicación válida.");
        return;
      }
      console.log("Marcando asistencia con ubicación:", location);
    }

    setLoading(true);
    const response = await addRemoteAttendance(employeeId, markType, location);
    setLoading(false);
    if (response.data.Type === 0) {
      setSuccessMsg(`Marcación de ${markType === 0 ? "entrada" : "salida"} registrada con éxito.`);
      fetchLastAttendance();
      fetchDailyActivity();
    } else {
      setErrorMsg(response.data.Message || "Error en la marcación.");
    }
  };

  useEffect(() => {
    if (!companyselected || !connectionId || employeeId) {
      return;
    }
    fetchLastAttendance();
  }, [companyselected, connectionId]);

  useEffect(() => {
    if (!companyselected || !connectionId || !employeeId) {
      return;
    }
    fetchDailyActivity();
  }, [companyselected, connectionId, employeeId]);

  const openGoogleMaps = () => {
    if (location) {
      window.open(
        `https://www.google.com/maps?q=${location.latitude},${location.longitude}&z=17`,
        "_blank"
      );
    }
  };

  return (
    <Grid container>
      <AppBar position="static">
        <Toolbar>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h6" color="white">
              Fecha y Hora Actual: {currentTime.toLocaleDateString()} {currentTime.toLocaleTimeString()}
            </Typography>
            {IS_LOCATION_REQUIRED && location && !locationError && (
              <Typography variant="body2" color="white">
                ✓ Ubicación detectada
              </Typography>
            )}
          </Grid>
        </Toolbar>
      </AppBar>

      <Grid container spacing={3} paddingTop={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: 2, height: "100%" }}>
            <Typography variant="h6">Última Marcación</Typography>
            {lastAttendance
              ? (
                <>
                  <Typography>Fecha: {lastAttendance.Date}</Typography>
                  <Typography>Hora: {lastAttendance.Time}</Typography>
                  <Typography>Tipo: {lastAttendance.MarkType}</Typography>
                </>
              )
              : employeeId === null
                ? (
                  <Typography>Cargando...</Typography>
                )
                : <></>
            }
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: 2 }}>
            <Typography variant="h6">Actividades Diarias</Typography>
            <TextField
              fullWidth
              multiline
              disabled={!employeeId}
              rows={4}
              value={dailyActivity}
              onChange={(e) => setDailyActivity(e.target.value)}
              placeholder="Describe tus actividades diarias..."
            />
            <Button
              disabled={!employeeId}
              variant="contained"
              color="primary"
              onClick={handleActivitySubmit}
              sx={{ marginTop: 2 }}
            >
              Guardar Actividades
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={3} sx={{ padding: 2, textAlign: "center" }}>
            <Button
              variant="contained"
              color="success"
              disabled={areButtonsDisabled()}
              onClick={() => handleAttendanceMark(0)}
              sx={{ marginRight: 2 }}
            >
              Marcar Entrada
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={areButtonsDisabled()}
              onClick={() => handleAttendanceMark(1)}
            >
              Marcar Salida
            </Button>

            {IS_LOCATION_REQUIRED && isLocationLoading && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 2 }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">Obteniendo ubicación...</Typography>
              </Box>
            )}

            {IS_LOCATION_REQUIRED && locationError && !isLocationLoading && (
              <Box sx={{ mt: 2 }}>
              <Typography color="error" variant="body2">
                {locationError}
              </Typography>
              {shouldRetry && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={requestLocationPermission}
                  sx={{ mt: 1 }}
                >
                  Reintentar Obtención de Ubicación
                </Button>
              )}
              {locationError.includes('denegado') && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Para habilitar los permisos manualmente, ve a la configuración de tu navegador, busca "Configuración del sitio" o "Permisos de ubicación" y actualiza los permisos para este sitio.
                </Typography>
              )}
            </Box>
            )}

            {IS_LOCATION_REQUIRED && location && !locationError && !isLocationLoading && (
              <Box sx={{ mt: 2 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setMapModalOpen(true)}
                  sx={{ cursor: "pointer" }}
                >
                  Ver ubicación detectada
                </Link>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Modal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        aria-labelledby="map-modal-title"
        aria-describedby="map-modal-description"
      >
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 800,
          height: "80%",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 0,
          borderRadius: 1,
          overflow: "hidden"
        }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Typography id="map-modal-title" variant="h6">
              Ubicación Detectada
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Latitud: {location?.latitude?.toFixed(6)}, Longitud: {location?.longitude?.toFixed(6)} {location?.accuracy && ` (Precisión: ${Math.round(location?.accuracy)}m)`}
            </Typography>
          </Box>
          <Box sx={{ width: '100%', minHeight: 'calc(100% - 150px)', height: 'calc(100% - 150px)' }}>
            {location && (
              <iframe
                title="OpenStreetMap"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${location.longitude - 0.01},${location.latitude - 0.01},${location.longitude + 0.01},${location.latitude + 0.01}&layer=mapnik&marker=${location.latitude},${location.longitude}`}
                allowFullScreen
              />
            )}
          </Box>
          <Box sx={{ p: 2, textAlign: "center", borderTop: 1, borderColor: "divider" }}>
            <Button
              variant="outlined"
              onClick={openGoogleMaps}
              sx={{ mr: 2 }}
            >
              Abrir en Google Maps
            </Button>
            <Button
              variant="contained"
              onClick={() => setMapModalOpen(false)}
            >
              Cerrar
            </Button>
          </Box>
        </Box>
      </Modal>

      <MySnackbar
        severity="error"
        msg={errorMsg}
        onClose={() => setErrorMsg("")}
        open={!!errorMsg}
      />

      <MySnackbar
        severity="success"
        msg={successMsg}
        onClose={() => {
          setSuccessMsg("");
        }}
        open={!!successMsg}
      />
    </Grid>
  );
};

export default AttendancePage;